/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { Alert, Snackbar } from "@mui/material";
import { useEffect, useState } from "react";
import { theme } from "styles/theme";
import Button from "uis/Button";
import Icon from "uis/Icon";
import Tooltip from "uis/Tooltip";
import InputField from "uis/InputField";
import {
  TScheduledVisitPurpose,
  getScheduledVisitPurpose,
  updateScheduledVisitPurpose,
} from "apis/visitors/setting/scheduled-visit/purpose";
import Root from "pages/root";
import UI from "@mile-meeting/mile-ui";

const Title = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;

  color: ${theme.neutral500};
  font-size: 17px;
  font-weight: 600;
`;

export const Container = styled.div`
  box-sizing: border-box;
  min-width: 1060px;
  display: flex;
  padding: 0px 20px 80px 20px;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
`;

export const Heading = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  align-self: stretch;
`;

const ScheduledVisit = () => {
  const [visitPurpose, setVisitPurpose] = useState<TScheduledVisitPurpose[]>([]);
  const [success, setSuccess] = useState<boolean>(false);

  const onHandleDelete = (index: number) => {
    setVisitPurpose([...visitPurpose].filter((item, id) => index !== id));
  };

  useEffect(() => {
    getScheduledVisitPurpose().then((res) => {
      if (res.success && res.data) {
        setVisitPurpose(res.data);
      }
    });
  }, []);

  return (
    <Container>
      <Heading>
        <UI.Typography
          type="title03"
          fontStyle="bold"
          color="var(--alias-color-label-neutral)"
          style={{
            padding: "24px 0 24px 20px",
          }}
        >
          사전 방문 관리
        </UI.Typography>
      </Heading>
      <div
        css={css`
          display: flex;
          flex-direction: column;
          width: 100%;
          box-sizing: border-box;
          flex: 1;
          padding: 30px;
          gap: 12px;
        `}
      >
        <Snackbar
          open={success}
          autoHideDuration={3000}
          onClose={() => setSuccess(false)}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert severity="success">설정이 업데이트 되었습니다.</Alert>
        </Snackbar>

        <Title>
          사전 방문 목적 설정
          <Tooltip title="" explain="사전 방문자 등록시, 방문자 목적 선택 목록에 설정한 목적 정보가 띄워집니다." />
        </Title>
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 12px;
            margin-top: 24px;
          `}
        >
          {visitPurpose.map((item, index) => {
            return (
              <CategoriesSetting
                key={index}
                placeholder="목적을 입력해주세요."
                value={item.content}
                onDelete={() => onHandleDelete(index)}
                onChangeTitle={(value) => {
                  const temp = [...visitPurpose];
                  temp[index].content = value;
                  setVisitPurpose(temp);
                }}
              />
            );
          })}
        </div>
        {visitPurpose.length < 9 ? (
          <div
            onClick={() => {
              setVisitPurpose((prev) => [...prev, {}]);
            }}
            css={css`
              margin-top: 16px;
              display: flex;
              gap: 4px;
              align-items: center;
              margin-left: auto;
              width: max-content;

              cursor: pointer;
            `}
          >
            <Icon name="plus-circle" color={theme.primary500} />
            <p
              css={css`
                color: ${theme.primary500};
                font-size: 16px;
                font-weight: 600;

                &:hover {
                  font-weight: 800;
                }
              `}
            >
              추가하기
            </p>
          </div>
        ) : null}
        <Button
          onClick={async () => {
            const purposes: TScheduledVisitPurpose[] = [...visitPurpose]
              .filter((item) => item.content)
              .map((item, index) => {
                return {
                  content: item.content,
                  displayOrder: index + 1,
                  id: item.id ? item.id : undefined,
                };
              });

            const res = await updateScheduledVisitPurpose(purposes);
            if (res) setSuccess(true);
          }}
          title="저장하기"
          styles={{
            container: css`
              width: max-content;
              padding: 12px 40px;
              margin-left: auto;
              margin-top: 24px;
            `,
          }}
        />
      </div>
    </Container>
  );
};

const CategoriesSetting = ({
  placeholder,
  onDelete,
  onChangeTitle = () => {},
  value,
}: {
  placeholder?: string;
  onDelete?: () => void;
  onChangeTitle?: (value: string) => void;
  value?: string;
}) => {
  return (
    <div
      css={css`
        display: flex;
        gap: 16px;
        align-items: center;
      `}
    >
      <div
        css={css`
          display: flex;
          gap: 12px;
          flex: 1;
        `}
      >
        <InputField
          value={value}
          onChange={(value) => onChangeTitle(value)}
          placeholder={placeholder}
          styles={{
            container: css`
              flex: 1;
            `,
          }}
        />
      </div>

      <Icon name="delete" color={theme.neutral400} hoverColor={theme.neutral500} onClick={onDelete} />
    </div>
  );
};

export default ScheduledVisit;
